// The cache sheet (v2-the-deep.md §9.3, §9.4, round 088 card 10, R2631,
// R2643, R2649).
//
// The cache sheet stands in the place of the rename card of the context
// slot (R1818), as the naming sheet of a seamount does (R2626). CACHES in
// the ⋯ of an own fleet card opens it for that fleet while `treasure`
// holds. The sheet states the fee of `CatalogRules.cache_fee_credits`,
// which the game destroys, the player caches of the commander this season
// against `cache_deposits_per_season`, and the finds of the world day
// against `cache_finds_per_world_day`. It holds the note: a phrase of
// `[ui.cache_note]`, and, while the seat holds the key `free_text`, a box
// for a free-text note of `CACHE_NOTE_MIN_RUNES` to `CACHE_NOTE_MAX_RUNES`
// runes. LEAVE CACHE sends one DEPOSIT of the fleet at its position. The
// list YOUR CACHES names each own player cache of `Empire.caches` with its
// finds and its note, and NOTE on a cache with no note opens the sheet in
// its note state, where SAVE NOTE sends one NOTE. The hub is the judge of
// the water, the cap, the fee, the key and the note; a refusal arrives as
// an error notice (`cache_*` of `[notices.hub]`).
//
// The find card is the third state of the sheet: the card of one cache of
// `Empire.cache_finds` for its finder, with the note of the depositor in
// the language of the reader. Only the finder reads it. A free-text note
// carries the ⚑ of REPORT, which files `REPORT_KIND_CACHE_NOTE`; a phrase
// carries none, because it cannot carry abuse (§9.4, R2631). The overlay
// offers the card of a new find with a note once, and YOUR FINDS of the
// leave sheet opens it again with READ.
//
// The SEARCH picker is the fourth state of the sheet (R2699): SEARCH on
// the peek of water opens it for one cell while no own fleet is selected.
// It lists the own fleets at sea, nearest first, as the commission card
// lists them for ASSIGN, and SEARCH on a row orders that fleet to the
// cell. A fleet with its cable out states why it takes no order. The leave
// sheet of a fleet carries SEARCH HERE, which orders the fleet to search
// the cell where it stands.
//
// Only a fit hull takes a SEARCH (v2-the-deep.md §9.6, R2834): a combat
// ship or a Dredger, the classes of `CatalogRules.work_fit_classes`. The
// picker lists fit hulls alone (`fitSea`), and the leave sheet of an unfit
// fleet carries no SEARCH HERE.
//
// SEARCH on the peek of a wreck that holds cargo, and SEARCH on a row of
// WRECKS WITH CARGO, open the SEARCH picker at the wreck point (R2860): a
// SEARCH there recovers the cargo of the wrecks in its reach, as it finds
// a cache. The overlay writes the distances of an open picker again on its
// 4 Hz pass, so a row follows its fleet (`pickerRows`).
//
// Everything here is pure, so a test reads the sheet with no overlay.

import { AnchorState, CacheKind, CacheOp, type CacheAction, type CacheRow, type ShipClass } from '../gen/proto/aquaphobia';
import { CACHE_NOTE_MAX_RUNES, CACHE_NOTE_MIN_RUNES } from '../gen/balance';
import { card as S, cacheNote as W } from '../gen/strings';
import { type GameState, num } from '../state';
import { esc } from './esc';
import { selectBox, type Choice } from './choice/choice';
import { buttonHtml, type CardSpec, type Fact } from './cards/card';
import { markSvg } from './cards/marks';
import { cellLabel } from './inbox/words';
import { runeCount } from '../util/text';
import { cellCenter } from '../world';

import { workFit } from './cards/orders';

export { CACHE_OPEN } from './cards/treasure';

/** The act of NOTE on a row of YOUR CACHES (the cache id). */
export const CACHE_NOTE_OPEN = 'cachenoteopen';
/** The act of READ on a row of YOUR FINDS (the cache id). */
export const CACHE_FIND_OPEN = 'cachefindopen';
/** The act of the phrase picker. */
export const CACHE_PHRASE = 'cachephrase';
/** The act of LEAVE CACHE and SAVE NOTE. */
export const CACHE_SAVE = 'cachesave';
/** CANCEL is the act of the rename card's CANCEL: one card, one close. */
const CACHE_CANCEL = 'renamecancel';
/** The act of SEARCH on a row of the SEARCH picker (the fleet id, and the
 *  cell as the count; the overlay reads the point of the picker). */
export const CACHE_SEARCH = 'cachesearch';
/** The act of SEARCH HERE on the leave sheet (the fleet id). */
export const CACHE_SEARCH_HERE = 'cachesearchhere';

/** One own fleet at sea, as the SEARCH picker reads it. The
 *  overlay names the class from the catalog. */
export interface SearchFleet {
  id: number;
  name: string;
  /** The class name, or '' when the catalog holds none. */
  kind: string;
  /** The hull class: only a fit class takes a SEARCH (§9.6). */
  cls: ShipClass;
  x: number;
  y: number;
  anchor: AnchorState;
}

/** What the sheet holds while it stands. `phrase` is the place of the
 *  picked phrase plus one, the wire's form; 0 picks none. */
export type CacheDraft =
  /** `fit`: the fleet takes a SEARCH, so the sheet carries
   *  SEARCH HERE (§9.6, R2834). */
  | { mode: 'leave'; fleetId: number; fleetName: string; phrase: number; freeText: boolean; fit: boolean }
  | { mode: 'note'; cacheId: number; phrase: number; freeText: boolean }
  | { mode: 'find'; cacheId: number }
  /** The SEARCH picker of a cell. `at` is the point of a wreck with cargo
   *  in world px (R2860); without it the fleet searches the cell centre. */
  | { mode: 'search'; cell: number; at?: readonly [number, number] };

/** The picker state of the sheet. */
export type PickerDraft = Extract<CacheDraft, { mode: 'search' }>;

/** Is the state the picker? */
export function isPicker(d: CacheDraft): d is PickerDraft {
  return d.mode === 'search';
}

/** Does a free text fit the length the server holds a note to? */
export function noteFits(text: string): boolean {
  const n = runeCount(text.trim());
  return n >= CACHE_NOTE_MIN_RUNES && n <= CACHE_NOTE_MAX_RUNES;
}

/** Why the text of a sheet goes nowhere, or '' when it may go: a free
 *  text of the wrong length. */
export function noteRefusal(d: CacheDraft, text: string): string {
  const t = text.trim();
  if (d.mode === 'find' || isPicker(d) || !d.freeText || t === '' || noteFits(t)) return '';
  return S.cache_note_bad_length({ min: CACHE_NOTE_MIN_RUNES, max: CACHE_NOTE_MAX_RUNES });
}

/**
 * The intent of the sheet: the free text while the seat holds the key and
 * the box holds text, else the picked phrase. LEAVE CACHE may carry no
 * note; SAVE NOTE with no note and a free text the server would refuse on
 * its length answer null, so the sheet sends nothing.
 */
export function cacheIntent(d: CacheDraft, text: string): CacheAction | null {
  if (d.mode === 'find' || isPicker(d) || noteRefusal(d, text)) return null;
  const t = d.freeText ? text.trim() : '';
  const phrase = t !== '' ? 0 : d.phrase;
  if (d.mode === 'leave') {
    return { op: CacheOp.CACHE_OP_DEPOSIT, fleetId: d.fleetId, cacheId: 0, text: t, phrase };
  }
  if (t === '' && phrase === 0) return null;
  return { op: CacheOp.CACHE_OP_NOTE, fleetId: 0, cacheId: d.cacheId, text: t, phrase };
}

/** The own player caches of this season, in the order of the wire. */
export function ownCaches(s: Pick<GameState, 'empire'>): readonly CacheRow[] {
  return s.empire?.caches ?? [];
}

/** The choices of the phrase picker: none, then each phrase by its place
 *  plus one. */
function phraseChoices(): Choice[] {
  return [{ id: '0', label: S.cache_note_none }, ...W.phrases.map((p, i) => ({ id: String(i + 1), label: p }))];
}

/** The note section: the picker and, with the key, the free-text box.
 *  The box is 16 px, so iOS does not zoom (R1818). */
function noteSection(d: Extract<CacheDraft, { mode: 'leave' | 'note' }>): string {
  return '<div class="ck-sect">'
    + `<div class="ck-sh ck-cap">${esc(S.cache_note_head)}</div>`
    + selectBox(phraseChoices(), String(d.phrase), CACHE_PHRASE, { label: S.cache_note_head, uid: 'cachephrase', size: 'compact' })
    + (d.freeText
      ? `<input class="renin" type="text" autocomplete="off" maxlength="${CACHE_NOTE_MAX_RUNES * 2}" `
        + `aria-label="${esc(S.cache_note_text)}" placeholder="${esc(S.cache_note_text)}">`
      : '')
    + '</div>';
}

/** The words of a note on a row of the depositor: the note itself, or the
 *  word of a cache with none. An operator's delete reads as none. */
function noteWords(c: CacheRow): string {
  return c.note ? S.cache_note_quoted({ note: c.note }) : S.cache_note_unset;
}

/** One row of YOUR CACHES: the place, the finds and the note, and NOTE
 *  while the cache has none. */
function ownRow(c: CacheRow): string {
  const press = c.noteSet ? '' : buttonHtml({
    act: CACHE_NOTE_OPEN, id: c.id, face: 'quiet', label: S.cache_note_btn, title: S.cache_note_btn_title,
  }, { keycaps: false });
  return `<div class="ck-row">${markSvg('cache', 'var(--text)')}`
    + `<span class="ck-rn"><b>${esc(cellLabel(c.cell))}</b>`
    + `<i>${esc(S.cache_finds({ n: c.finds }))} · ${esc(noteWords(c))}</i></span>${press}</div>`;
}

/** One list section of the leave sheet under its head, or '' for an
 *  empty list: YOUR CACHES and YOUR FINDS. */
function listSection(head: string, list: readonly CacheRow[], rowOf: (c: CacheRow) => string): string {
  if (list.length === 0) return '';
  return `<div class="ck-sect"><div class="ck-sh ck-cap">${esc(head)}</div>`
    + `<div class="ck-list">${list.map(rowOf).join('')}</div></div>`;
}

/** The caches a hull of this commander took this season, in the order of
 *  the wire. */
export function ownFinds(s: Pick<GameState, 'empire'>): readonly CacheRow[] {
  return s.empire?.cacheFinds ?? [];
}

/** The title of a find: the name an operator gave an event cache, else
 *  the word of its kind. */
function findTitle(c: CacheRow): string {
  return c.label || cacheKindTitle(c.kind);
}

/** One row of YOUR FINDS: the kind and the place, and READ where the
 *  depositor left a note. */
function findRow(c: CacheRow): string {
  const press = c.note ? buttonHtml({
    act: CACHE_FIND_OPEN, id: c.id, face: 'quiet', label: S.cache_read_btn, title: S.cache_read_btn_title,
  }, { keycaps: false }) : '';
  return `<div class="ck-row">${markSvg('cache', 'var(--text)')}`
    + `<span class="ck-rn"><b>${esc(findTitle(c))}</b><i>${esc(cellLabel(c.cell))}</i></span>${press}</div>`;
}

/**
 * The find card (round 088 card 10, "the finder: the cache note"): the
 * kind, the place, the chip FOUND, the finds of the day, and the note of
 * the depositor of a player cache with `reportMark` beside a free-text
 * note. A find that left the list answers null.
 */
export function findCard(s: Pick<GameState, 'empire' | 'rules'>, cacheId: number, reportMark: (c: CacheRow) => string): CardSpec | null {
  const c = ownFinds(s).find((x) => x.id === cacheId);
  if (!c) return null;
  const player = c.kind === CacheKind.CACHE_KIND_PLAYER;
  let body = '';
  if (player) {
    const note = c.note
      ? `<p class="ck-desc"><q>${esc(c.note)}</q>${c.freeNote ? reportMark(c) : ''}</p>`
      : `<p class="ck-desc">${esc(S.cache_note_none_left)}</p>`;
    body = '<div class="ck-sect">'
      + `<div class="ck-sh ck-cap"><span>${esc(S.cache_note_head)}</span><span class="ck-shr">${esc(S.cache_note_only_you)}</span></div>`
      + `${note}</div>`;
  }
  return {
    size: 'card', mark: markSvg('cache', 'var(--text)'), title: findTitle(c), ink: 'neutral',
    kind: cellLabel(c.cell), status: { text: S.cache_found_chip, tone: 'good' }, close: false,
    facts: [todayFact(s)],
    body,
    note: { lines: [esc(player ? S.cache_find_player_note : S.cache_find_note)] },
    actions: [
      { act: 'focuscell', n: c.cell, face: 'dark', label: S.cache_show },
      { act: CACHE_CANCEL, label: S.cache_close, face: 'quiet' },
    ],
  };
}

/**
 * The finds the overlay offers: each find with a note that `seen` does not
 * hold, in the order of the wire. The first Empire frame of a page marks
 * every find it holds as seen, so a reload offers nothing again.
 */
export function freshNotes(finds: readonly CacheRow[], seen: ReadonlySet<number>): CacheRow[] {
  return finds.filter((c) => c.note !== '' && !seen.has(c.id));
}

/** The fact of the finds of the world day against its cap. */
function todayFact(s: Pick<GameState, 'empire' | 'rules'>): Fact {
  return { key: S.f_cache_today, val: esc(S.cache_of({ n: s.empire?.cacheFindsToday ?? 0, max: s.rules.cacheFindsPerWorldDay })) };
}

/** The facts of the leave sheet: the fee, the caches of the season and the
 *  finds of the day. */
function leaveFacts(s: Pick<GameState, 'empire' | 'rules'>): Fact[] {
  const r = s.rules;
  return [
    { key: S.f_cache_fee, val: esc(S.cache_fee({ n: r.cacheFeeCredits })) },
    { key: S.f_cache_season, val: esc(S.cache_of({ n: ownCaches(s).length, max: r.cacheDepositsPerSeason })) },
    todayFact(s),
  ];
}

/** The fleets at sea that take a SEARCH (§9.6, R2834): a combat ship or a
 *  Dredger, by `CatalogRules.work_fit_classes`. The picker lists these
 *  alone. */
export function fitSea(s: Pick<GameState, 'rules'>, sea: readonly SearchFleet[]): SearchFleet[] {
  return sea.filter((f) => workFit(s, f.cls));
}

/** Why a fleet takes no SEARCH, or '' when it takes one. The
 *  hub judges the order; the picker states the one reason the frame
 *  carries: the cable (`workOrderable` of the hub). */
function searchUnfit(f: SearchFleet): string {
  return f.anchor !== AnchorState.ANCHOR_STATE_UNSPECIFIED ? S.search_unfit_cable : '';
}

/** The point of a picker in world px: the wreck point when the picker has
 *  one (R2860), else the centre of the cell. */
export function pickerPoint(d: PickerDraft): readonly [number, number] {
  return d.at ?? cellCenter(d.cell);
}

/** One row of a picker: the fleet, its distance to the point and why it
 *  takes no order ('' when it takes one). */
export interface PickerRow {
  f: SearchFleet;
  px: number;
  why: string;
}

/**
 * The rows of a picker, nearest to its point first, the fleets that take
 * the order before the ones that do not (R2699, R2722). The overlay reads
 * them again on its 4 Hz pass to follow a fleet.
 */
export function pickerRows(d: PickerDraft, sea: readonly SearchFleet[]): PickerRow[] {
  const [cx, cy] = pickerPoint(d);
  return sea.map((f) => ({ f, px: Math.hypot(f.x - cx, f.y - cy), why: searchUnfit(f) }))
    .sort((a, b) => Number(a.why !== '') - Number(b.why !== '') || a.px - b.px || a.f.id - b.f.id);
}

/** The small line of a row: the class and the distance. */
export function pickerSub(r: PickerRow): string {
  return [r.f.kind, S.search_fit_away({ px: num(r.px) })].filter(Boolean).join(' · ');
}

/** The press of a row: SEARCH at the point of the picker. */
function pickerPress(d: PickerDraft, f: SearchFleet): string {
  return buttonHtml({
    act: CACHE_SEARCH, n: d.cell, id: f.id, face: 'dark', label: S.search_btn, title: S.search_btn_title({ name: f.name }),
  }, { keycaps: false });
}

/** One row of a picker: the fleet, its class and distance, and the press
 *  or why it takes none. */
function pickerRow(d: PickerDraft, r: PickerRow): string {
  const ink = r.why ? 'var(--dim)' : 'var(--text)';
  const end = r.why ? `<span class="ck-rf"><small>${esc(r.why)}</small></span>` : pickerPress(d, r.f);
  return `<div class="ck-row${r.why ? ' no' : ''}">${markSvg('cache', ink)}`
    + `<span class="ck-rn"><b style="color:${ink}">${esc(r.f.name)}</b><i>${esc(pickerSub(r))}</i></span>${end}</div>`;
}

/**
 * The SEARCH picker (R2699): the own fleets at sea for one point, nearest
 * first, the fleets that take the order before the ones that do not. At a
 * wreck point (R2860) the title and the note name the wreck.
 */
function pickerSheet(d: PickerDraft, sea: readonly SearchFleet[]): CardSpec {
  const rows = pickerRows(d, sea);
  const wreck = d.at !== undefined;
  const body = rows.length === 0
    ? `<div class="ck-sect"><p class="ck-desc">${esc(S.search_no_fleet)}</p></div>`
    : `<div class="ck-sect"><div class="ck-sh ck-cap">${esc(S.search_fit_head)}</div>`
      + `<div class="ck-list">${rows.map((r) => pickerRow(d, r)).join('')}</div></div>`;
  return {
    size: 'card', mark: markSvg('cache', 'var(--text)'), title: wreck ? S.search_wreck_sheet_title : S.search_sheet_title,
    ink: 'neutral', kind: cellLabel(d.cell), close: false,
    body,
    note: { lines: [esc(S.search_sheet_hint)] },
    actions: [
      { act: 'focuscell', n: d.cell, face: 'quiet', label: S.cache_show },
      { act: CACHE_CANCEL, label: S.cache_cancel, face: 'quiet' },
    ],
  };
}

/**
 * The cache sheet (round 088 card 10): LEAVE A CACHE for one fleet, the
 * note of one own cache, the find card of one find, or the SEARCH picker
 * of one cell or wreck point (R2699, R2860). A state whose cache left its
 * list answers null, and the sheet then closes.
 */
export function cacheSheet(
  s: Pick<GameState, 'empire' | 'rules'>, d: CacheDraft, reportMark: (c: CacheRow) => string = () => '',
  sea: readonly SearchFleet[] = [],
): CardSpec | null {
  if (d.mode === 'find') return findCard(s, d.cacheId, reportMark);
  if (isPicker(d)) return pickerSheet(d, sea);
  const lines: string[] = [];
  if (d.freeText) lines.push(esc(S.cache_note_hint_text({ min: CACHE_NOTE_MIN_RUNES, max: CACHE_NOTE_MAX_RUNES })));
  if (d.mode === 'note') {
    const c = ownCaches(s).find((x) => x.id === d.cacheId);
    if (!c) return null;
    lines.unshift(esc(S.cache_note_once));
    return {
      size: 'card', mark: markSvg('cache', 'var(--text)'), title: S.cache_note_title, ink: 'neutral',
      kind: cellLabel(c.cell), close: false,
      facts: [{ key: S.f_cache_finds, val: esc(S.cache_finds({ n: c.finds })) }],
      body: noteSection(d),
      note: { lines },
      actions: [
        { act: CACHE_SAVE, label: S.cache_note_save, face: 'dark' },
        { act: CACHE_CANCEL, label: S.cache_cancel, face: 'quiet' },
      ],
    };
  }
  const full = ownCaches(s).length >= s.rules.cacheDepositsPerSeason && s.rules.cacheDepositsPerSeason > 0;
  lines.unshift(esc(S.cache_leave_hint));
  return {
    size: 'card', mark: markSvg('cache', 'var(--text)'), title: S.cache_leave_title, ink: 'neutral',
    kind: S.cache_leave_kind({ fleet: d.fleetName }), close: false,
    facts: leaveFacts(s),
    body: noteSection(d) + listSection(S.cache_yours_head, ownCaches(s), ownRow)
      + listSection(S.cache_finds_head, ownFinds(s), findRow),
    note: { lines },
    actions: [
      { act: CACHE_SAVE, label: S.cache_leave_btn, face: 'dark', off: full, title: full ? S.cache_full_title : S.cache_leave_btn_title },
      // SEARCH HERE (R2699): the fleet of the sheet searches its own cell,
      // when it is a fit hull (§9.6, R2834).
      ...(d.fit ? [{ act: CACHE_SEARCH_HERE, id: d.fleetId, label: S.cache_search_here, face: 'quiet' as const, title: S.cache_search_here_title }] : []),
      { act: CACHE_CANCEL, label: S.cache_cancel, face: 'quiet' },
    ],
  };
}

/** The title of a cache by its kind: the word of each kind of §9.1. */
export function cacheKindTitle(kind: CacheKind): string {
  switch (kind) {
    case CacheKind.CACHE_KIND_SEEDED: return S.cache_kind_seeded;
    case CacheKind.CACHE_KIND_RUIN: return S.cache_kind_ruin;
    case CacheKind.CACHE_KIND_PIRATE: return S.cache_kind_pirate;
    case CacheKind.CACHE_KIND_PLAYER: return S.cache_kind_player;
    case CacheKind.CACHE_KIND_EVENT: return S.cache_kind_event;
  }
  return S.cache_kind_other;
}
